/**
 * Escopo do ciclo: quais dos 18 setores EXISTEM na unidade (doc 17b). Declarado
 * uma vez, antes do checklist, e auditável. "Fora de escopo (setor inexistente)"
 * ≠ "NA (item)": o primeiro é do ciclo (o setor não existe), o segundo é
 * julgamento item a item dentro de um setor que existe. Ambos saem do cálculo,
 * mas têm origem e rótulo diferentes — o dado/relatório nunca os confunde.
 *
 * Tipos + value object + migração de compatibilidade. Sem IO; não importa o
 * catálogo (`@caint/data` depende de `@caint/shared`), então `itensAplicaveis`
 * recebe o catálogo por parâmetro. Config nomeada, sem número mágico (doc 13.8).
 */

import type { ItemCatalogo } from "./catalogo.ts";

/** Código de setor do instrumento (05A..05R). */
export type SetorCodigo = string;

/** Setores que não podem ser desmarcados (a unidade é obstétrica e tem
 *  governança). Parametrizável e versionado (doc 13.8): Governança 05A +
 *  Obstetrícia 05C por padrão. */
export const SETORES_OBRIGATORIOS: readonly SetorCodigo[] = Object.freeze(["05A", "05C"]);

/** Forma de um código de setor válido — 05A..05R, opcionalmente com sufixo
 *  numérico para setores desdobrados (ex.: 05E2/05E3 = Enfermaria 2/3).
 *  Validação self-contained (não depende do catálogo) na borda do value object. */
const SETOR_REGEX = /^05[A-R][0-9]?$/;

/** Uma (de)seleção de setor na trilha de auditoria (doc 15 §15.6). */
export interface EscopoLogEntry {
  readonly setor: SetorCodigo;
  readonly ativo: boolean;
  readonly por: string;
  readonly em: string; // ISO-8601
}

/** Escopo de uma avaliação/ciclo (blob persistido/sincronizado). */
export interface EscopoData {
  readonly setoresAtivos: readonly SetorCodigo[];
  readonly escopoConfirmado: boolean;
  readonly escopoConfirmadoPor?: string;
  readonly escopoConfirmadoEm?: string; // ISO-8601
  readonly escopoLog: readonly EscopoLogEntry[];
  /**
   * Liberação manual da amostra cega (Supervisão/AUDIN): quando `true`, a amostra
   * é liberada mesmo com o checklist abaixo da trava estrita (< 90% com evidência).
   * Só a Supervisão liga isto, num ciclo específico; default `undefined` = travado.
   */
  readonly amostraLiberada?: boolean;
}

/** Garante que os obrigatórios estejam presentes, sem duplicar, preservando ordem. */
export function comObrigatorios(
  setores: Iterable<SetorCodigo>,
  obrigatorios: readonly SetorCodigo[] = SETORES_OBRIGATORIOS,
): SetorCodigo[] {
  const set = new Set<SetorCodigo>(setores);
  for (const o of obrigatorios) {
    set.add(o);
  }
  return [...set];
}

/**
 * Escopo inicial pré-sugerido para um ciclo novo: a seleção sugerida (do ciclo
 * anterior ou do cadastro) UNIDA aos obrigatórios, ainda NÃO confirmada (o gate
 * exige confirmação explícita). A sugestão é editável; os obrigatórios não.
 */
export function escopoInicial(
  sugeridos: Iterable<SetorCodigo>,
  obrigatorios: readonly SetorCodigo[] = SETORES_OBRIGATORIOS,
): EscopoData {
  return {
    setoresAtivos: comObrigatorios(sugeridos, obrigatorios),
    escopoConfirmado: false,
    escopoLog: [],
  };
}

/**
 * Compatibilidade retroativa (doc 17b §4): avaliação legada SEM escopo (ou com
 * `setoresAtivos` vazio) assume TODOS os setores ativos e `escopoConfirmado =
 * true` — o comportamento atual é preservado e quem já avaliava não é bloqueado.
 * Idempotente: dados já no formato novo passam intactos (só normaliza `escopoLog`).
 */
export function migrarEscopo(
  dados: Partial<EscopoData> | null | undefined,
  setoresTodos: readonly SetorCodigo[],
): EscopoData {
  if (!dados || !dados.setoresAtivos || dados.setoresAtivos.length === 0) {
    return {
      setoresAtivos: [...setoresTodos],
      escopoConfirmado: true,
      escopoLog: [],
    };
  }
  return {
    setoresAtivos: dados.setoresAtivos,
    escopoConfirmado: dados.escopoConfirmado ?? false,
    escopoConfirmadoPor: dados.escopoConfirmadoPor,
    escopoConfirmadoEm: dados.escopoConfirmadoEm,
    escopoLog: dados.escopoLog ?? [],
    amostraLiberada: dados.amostraLiberada,
  };
}

/**
 * Resolve o escopo EFETIVO de uma avaliação a partir das fontes possíveis, com
 * **last-write-wins por `escopoConfirmadoEm`** entre a edição local (offline) e o
 * escopo sincronizado do servidor (doc 17b §4/§4b). Sem isso, um escopo local
 * desatualizado mascararia para sempre uma edição feita pela gestão na página de
 * Ciclos (`PUT /avaliacoes/:id/escopo`). Empate ⇒ servidor vence (a gestão é a
 * autoridade). Sem nenhuma fonte: compat (já tem respostas ⇒ todos + confirmado;
 * doc 17b §4) ou ciclo novo (sugestão NÃO confirmada — o gate exige confirmação).
 */
export function resolverEscopo(
  local: Partial<EscopoData> | undefined | null,
  servidor: Partial<EscopoData> | undefined | null,
  temRespostas: boolean,
  sugeridos: Iterable<SetorCodigo>,
  setoresTodos: readonly SetorCodigo[],
): EscopoData {
  const temLocal = !!local?.setoresAtivos?.length;
  const temServidor = !!servidor?.setoresAtivos?.length;
  if (temLocal && temServidor) {
    const tl = local?.escopoConfirmadoEm ?? "";
    const ts = servidor?.escopoConfirmadoEm ?? "";
    return migrarEscopo(ts >= tl ? servidor : local, setoresTodos);
  }
  if (temLocal) {
    return migrarEscopo(local, setoresTodos);
  }
  if (temServidor) {
    return migrarEscopo(servidor, setoresTodos);
  }
  if (temRespostas) {
    return migrarEscopo(undefined, setoresTodos); // compat: todos + confirmado
  }
  return escopoInicial(sugeridos);
}

export class EscopoInvalido extends Error {
  constructor(motivo: string) {
    super(`Escopo inválido: ${motivo}`);
    this.name = "EscopoInvalido";
  }
}

/**
 * Value object do escopo (doc 13 §13.4): valida que os obrigatórios estão no
 * conjunto ativo e que todo código é um setor válido (05A..05R). Expõe
 * `contemSetor` e `itensAplicaveis` (recorte do catálogo pelos setores ativos).
 */
export class EscopoAvaliacao {
  private readonly ativos: ReadonlySet<SetorCodigo>;

  private constructor(
    readonly data: EscopoData,
    readonly obrigatorios: readonly SetorCodigo[],
  ) {
    this.ativos = new Set(data.setoresAtivos);
  }

  /** Constrói validando; lança {@link EscopoInvalido} em código/obrigatório inválido. */
  static de(data: EscopoData, obrigatorios: readonly SetorCodigo[] = SETORES_OBRIGATORIOS): EscopoAvaliacao {
    for (const cod of data.setoresAtivos) {
      if (!SETOR_REGEX.test(cod)) {
        throw new EscopoInvalido(`código de setor desconhecido: ${cod}`);
      }
    }
    const ativos = new Set(data.setoresAtivos);
    for (const o of obrigatorios) {
      if (!ativos.has(o)) {
        throw new EscopoInvalido(`setor obrigatório ausente: ${o}`);
      }
    }
    return new EscopoAvaliacao(data, obrigatorios);
  }

  get setoresAtivos(): readonly SetorCodigo[] {
    return this.data.setoresAtivos;
  }

  get confirmado(): boolean {
    return this.data.escopoConfirmado;
  }

  contemSetor(cod: SetorCodigo): boolean {
    return this.ativos.has(cod);
  }

  ehObrigatorio(cod: SetorCodigo): boolean {
    return this.obrigatorios.includes(cod);
  }

  /** Recorte do catálogo: só itens cujo `sector ∈ setoresAtivos`. */
  itensAplicaveis<T extends Pick<ItemCatalogo, "sector">>(catalogo: readonly T[]): T[] {
    return catalogo.filter((it) => this.ativos.has(it.sector));
  }

  /** Nº de itens aplicáveis (universo avaliável) — base do `tamanhoAmostraEfetivo`. */
  contarAplicaveis(catalogo: readonly Pick<ItemCatalogo, "sector">[]): number {
    return catalogo.reduce((n, it) => (this.ativos.has(it.sector) ? n + 1 : n), 0);
  }
}
